<template>
  <Teleport to="body">
    <transition name="dialog-fade">
      <div v-if="visible" class="mute-dialog-overlay" @click.self="close">
        <transition name="dialog-scale">
          <div v-if="visible" class="mute-dialog">
            <!-- Header -->
            <div class="dialog-header">
              <i class="bi" :class="isMuted ? 'bi-bell' : 'bi-bell-slash'"></i>
              <h3>{{ dialogTitle }}</h3>
            </div>
            
            <!-- Content -->
            <div class="dialog-content">
              <p class="dialog-description">{{ dialogDescription }}</p>
              
              <!-- Mute Options (only when muting) -->
              <div v-if="!isMuted" class="mute-options">
                <label 
                  v-for="option in muteOptions" 
                  :key="option.value"
                  class="mute-option"
                  :class="{ selected: selectedDuration === option.value }"
                >
                  <input 
                    type="radio" 
                    :value="option.value" 
                    v-model="selectedDuration"
                    class="mute-radio"
                  />
                  <span class="option-label">{{ option.label }}</span>
                  <span class="option-description">{{ option.description }}</span>
                </label>
              </div>
            </div>
            
            <!-- Actions -->
            <div class="dialog-actions">
              <button class="btn-cancel" @click="close">
                إلغاء
              </button>
              <button 
                class="btn-confirm" 
                :class="{ 'btn-unmute': isMuted }"
                :disabled="loading"
                @click="confirm"
              >
                <span v-if="loading" class="spinner-sm"></span>
                <span v-else>{{ confirmButtonText }}</span>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { ChatService } from '@/services/chat';

export default {
  name: 'MuteChatDialog',
  
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: String,
      required: true,
    },
    isMuted: {
      type: Boolean,
      default: false,
    },
    conversationName: {
      type: String,
      default: '',
    },
  },
  
  emits: ['close', 'muted', 'unmuted'],
  
  data() {
    return {
      loading: false,
      selectedDuration: '8h',
      muteOptions: [
        {
          value: '8h',
          label: '8 ساعات',
          description: 'كتم الإشعارات لمدة 8 ساعات',
        },
        {
          value: '1w',
          label: 'أسبوع',
          description: 'كتم الإشعارات لمدة أسبوع',
        },
        {
          value: 'always',
          label: 'دائماً',
          description: 'كتم الإشعارات حتى تقوم بإلغاء الكتم',
        },
      ],
    };
  },
  
  computed: {
    dialogTitle() {
      return this.isMuted
        ? 'إلغاء كتم المحادثة'
        : 'كتم المحادثة';
    },
    
    dialogDescription() {
      if (this.isMuted) {
        return 'ستستقبل إشعارات للرسائل الجديدة في هذه المحادثة';
      }
      return 'لن تستقبل إشعارات للرسائل الجديدة في هذه المحادثة';
    },
    
    confirmButtonText() {
      return this.isMuted
        ? 'إلغاء الكتم'
        : 'كتم';
    },
  },
  
  watch: {
    visible(val) {
      if (val) {
        document.body.style.overflow = 'hidden';
        this.selectedDuration = '8h';
      } else {
        document.body.style.overflow = '';
      }
    },
  },
  
  methods: {
    async confirm() {
      this.loading = true;
      
      try {
        if (this.isMuted) {
          // Unmute
          const result = await ChatService.unmuteConversation(this.conversationId);
          if (result.success) {
            this.$emit('unmuted');
            this.close();
          } else {
            console.error('[MuteChatDialog] Unmute failed:', result.message);
          }
        } else {
          // Mute
          const result = await ChatService.muteConversation(
            this.conversationId, 
            this.selectedDuration
          );
          if (result.success) {
            this.$emit('muted', this.selectedDuration);
            this.close();
          } else {
            console.error('[MuteChatDialog] Mute failed:', result.message);
          }
        }
      } catch (e) {
        console.error('[MuteChatDialog] Error:', e);
      } finally {
        this.loading = false;
      }
    },
    
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Overlay */
.mute-dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* Dialog */
.mute-dialog {
  background: var(--bs-body-bg, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

/* Header */
.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--bs-border-color, #e5e5e5);
}

.dialog-header i {
  font-size: 24px;
  color: var(--bs-success, #198754);
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--bs-body-color, #333);
}

/* Content */
.dialog-content {
  padding: 20px;
}

.dialog-description {
  margin: 0 0 16px;
  font-size: 14px;
  color: var(--bs-secondary-color, #6c757d);
  line-height: 1.5;
}

/* Mute Options */
.mute-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mute-option {
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  border: 2px solid var(--bs-border-color, #e5e5e5);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.mute-option:hover {
  border-color: var(--bs-success, #198754);
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

.mute-option.selected {
  border-color: var(--bs-success, #198754);
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

.mute-radio {
  display: none;
}

.option-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--bs-body-color, #333);
  margin-bottom: 2px;
}

.option-description {
  font-size: 12px;
  color: var(--bs-secondary-color, #6c757d);
}

/* Actions */
.dialog-actions {
  display: flex;
  gap: 12px;
  padding: 16px 20px 20px;
  border-top: 1px solid var(--bs-border-color, #e5e5e5);
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-cancel {
  background: var(--bs-tertiary-bg, #f5f5f5);
  color: var(--bs-body-color, #333);
}

.btn-cancel:hover {
  background: var(--bs-secondary-bg, #e9ecef);
}

.btn-confirm {
  background: var(--bs-warning, #ffc107);
  color: #000;
}

.btn-confirm:hover {
  background: var(--bs-warning-dark, #e0a800);
}

.btn-confirm.btn-unmute {
  background: var(--bs-success, #198754);
  color: #fff;
}

.btn-confirm.btn-unmute:hover {
  background: var(--bs-success-dark, #157347);
}

.btn-confirm:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Spinner */
.spinner-sm {
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Transitions */
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.3s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

.dialog-scale-enter-active,
.dialog-scale-leave-active {
  transition: all 0.3s ease;
}

.dialog-scale-enter-from,
.dialog-scale-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Dark mode */
[data-bs-theme="dark"] .mute-dialog {
  background: var(--bs-body-bg);
}
</style>
